<template>
  <div class="login-page">
    <div class="login-card">
      <!-- ☕ Logo / Icon -->
      <div class="logo">
        <div class="logo-circle">☕</div>
      </div>

      <h2 class="title">Café Manager</h2>
      <p class="subtitle">Connectez-vous à votre compte</p>

      <!-- 🔐 Form -->
      <form @submit.prevent="login" class="form">
        <div class="input-group">
          <span class="input-icon">
            <i class="bi bi-person-fill"></i>
          </span>
          <input
            v-model="email"
            type="text"
            class="input-field"
            placeholder="Nom d'utilisateur ou email"
            :disabled="loading"
            autocomplete="username"
          />
        </div>

        <div class="input-group">
          <span class="input-icon">
            <i class="bi bi-lock-fill"></i>
          </span>
          <input
            v-model="password"
            type="password"
            class="input-field"
            placeholder="Mot de passe"
            :disabled="loading"
            autocomplete="current-password"
          />
        </div>

        <!-- ❌ Error message -->
        <transition name="fade">
          <div v-if="error" class="error-box">
            <i class="bi bi-exclamation-circle-fill"></i>
            <span>{{ error }}</span>
          </div>
        </transition>

        <!-- 🔘 Login button with loader -->
        <button
          type="submit"
          class="btn-login"
          :disabled="loading || !email || !password"
        >
          <span v-if="loading" class="spinner">
            <i class="bi bi-arrow-repeat spin"></i>
            Connexion...
          </span>
          <span v-else>
            <i class="bi bi-box-arrow-in-right"></i>
            Se connecter
          </span>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "Login",
  data() {
    return {
      email: "",
      password: "",
      error: "",
      loading: false,
    };
  },
  methods: {
    ...mapActions("auth", ["loginUser"]),
    async login() {
      if (!this.email || !this.password) {
        this.error = "Veuillez remplir tous les champs";
        return;
      }

      this.loading = true;
      this.error = "";

      try {
        await this.loginUser({ email: this.email, password: this.password });
        this.$router.push("/").catch(() => {});
      } catch (e) {
        this.error = e.message || "Identifiants invalides";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* 🌍 Full page background */
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: "Poppins", Avenir, Helvetica, Arial, sans-serif;
}

/* 💳 Login card */
.login-card {
  background: #ffffff;
  border-radius: 24px;
  padding: 40px 32px;
  width: 100%;
  max-width: 380px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.5s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ☕ Logo */
.logo {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}
.logo-circle {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

/* 📝 Title & subtitle */
.title {
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}
.subtitle {
  text-align: center;
  font-size: 14px;
  color: #718096;
  margin: 6px 0 28px;
}

/* 🧾 Form */
.form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* 📥 Input group */
.input-group {
  position: relative;
  display: flex;
  align-items: center;
}
.input-icon {
  position: absolute;
  left: 14px;
  color: #a0aec0;
  font-size: 16px;
  z-index: 2;
}
.input-field {
  width: 100%;
  padding: 12px 14px 12px 42px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  color: #2d3748;
  background: #f7fafc;
  transition: all 0.2s ease;
  outline: none;
}
.input-field:focus {
  border-color: #667eea;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
}
.input-field:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ❌ Error box */
.error-box {
  background: #fed7d7;
  color: #c53030;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 🔘 Login button */
.btn-login {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #667eea, #764ba2);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.btn-login:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}
.btn-login:active:not(:disabled) {
  transform: translateY(0);
}
.btn-login:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* 🔄 Spinner */
.spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ✨ Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
